/**
 * Evolución por día: enviados, entregados, leídos y fallidos; el interruptor «Respuestas y
 * bajas» cambia a esas dos series. Una sola escala (nunca doble eje): las dos vistas miden
 * mensajes, pero en órdenes de magnitud distintos, por eso son dos vistas y no cuatro líneas más.
 *
 * Las bajas van por el día en que SALIÓ el mensaje (como entregados y leídos): así la serie suma lo mismo que el
 * recuadro «Bajas». Las respuestas, por el día en que llegaron. Se dice debajo del gráfico.
 */

import { useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { FilaDia } from "../../types.estadisticas";
import { COLORES, TEXTO_SIN_ENVIOS, ddmm } from "../../logic/estadisticas";
import { ESTILO_TOOLTIP, Leyenda, Vacio } from "./comun";

type Vista = "envios" | "respuestas";
const SERIES: Record<Vista, { clave: keyof FilaDia; label: string; color: string }[]> = {
  envios: [
    { clave: "enviados", label: "Enviados", color: COLORES.enviados },
    { clave: "entregados", label: "Entregados", color: COLORES.entregados },
    { clave: "leidos", label: "Leídos", color: COLORES.leidos },
    { clave: "fallidos", label: "Fallidos", color: COLORES.fallidos },
  ],
  respuestas: [
    { clave: "respuestas", label: "Respuestas", color: COLORES.respuestas },
    { clave: "bajas", label: "Bajas", color: COLORES.bajas },
  ],
};

export function GraficoSerieDiaria({ serie, alto = 220 }: { serie: FilaDia[]; alto?: number }) {
  const [vista, setVista] = useState<Vista>("envios");
  const hayEnvios = (serie || []).some((f) => (Number(f.enviados) || 0) > 0 || (Number(f.respuestas) || 0) > 0);
  if (!hayEnvios) return <Vacio>{TEXTO_SIN_ENVIOS}.</Vacio>;
  const datos = serie.map((f) => ({ ...f, etiqueta: ddmm(f.fecha) }));
  const series = SERIES[vista];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Leyenda items={series.map((s) => ({ color: s.color, label: s.label, linea: true }))} />
        <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
          <input type="checkbox" checked={vista === "respuestas"} onChange={(e) => setVista(e.target.checked ? "respuestas" : "envios")}
            className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
          Respuestas y bajas
        </label>
      </div>
      <div style={{ width: "100%", height: alto }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datos} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid stroke={COLORES.grilla} vertical={false} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} width={48} />
            <Tooltip {...ESTILO_TOOLTIP} formatter={(v) => Number(v).toLocaleString("es-AR")} />
            {series.map((s) => (
              <Line key={String(s.clave)} type="monotone" dataKey={String(s.clave)} name={s.label} stroke={s.color} strokeWidth={2}
                dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {vista === "respuestas" && (
        <p className="text-[11px] text-slate-400">Las bajas van por el día en que salió el mensaje y suman lo mismo que «Bajas»; las respuestas, por el día en que llegaron.</p>
      )}
    </div>
  );
}

export default GraficoSerieDiaria;
